import api from "./api";

export type ApiError =
  | { type: "serverValidation"; errors?: Record<string, string> }
  | { type: "network"; message?: string }
  | { type: "server"; message?: string };

export interface SunbedPricing {
  label: string;
  type: "per_minute" | "fixed";
  duration?: number;
  price: number;
}

export interface CreateSunbedPayload {
  label: string;
  status: string;
  sunbed_room: string[];
  pricing: SunbedPricing[];
}

export interface CreateSunbedResponse {
  message: string;
  sunbed: any;
}

export const createSunbed = async (
  payload: CreateSunbedPayload
): Promise<CreateSunbedResponse> => {
  const errors: Record<string, string> = {};
  if (!payload.label) errors.label = "Label is required";
  if (!payload.status) errors.status = "Status is required";
  if (!payload.pricing || payload.pricing.length === 0)
    errors.pricing = "At least one pricing item is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }
  console.log(payload);
  try {
    const response = await api.post(
      `/admin/sunbeds`,
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Sunbed created:", response.data);

    return {
      message: response.data.message,
      sunbed: response.data.data,
    };
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      console.error("Network error in createSunbed:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    console.error("Create sunbed failed:", err.response.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface UpdateSunbedPayload {
  label: string;
  status: string;
  pricing: SunbedPricing[];
}

export const updateSunbed = async (
  id: number,
  payload: UpdateSunbedPayload
): Promise<CreateSunbedResponse> => {
  const errors: Record<string, string> = {};

  if (!payload.label) errors.label = "Label is required";
  if (!payload.status) errors.status = "Status is required";
  if (!payload.pricing || payload.pricing.length === 0)
    errors.pricing = "At least one pricing item is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.put(
      `/admin/sunbeds/${id}`,
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Sunbed updated:", response.data);

    return {
      message: response.data.message,
      sunbed: response.data.data,
    };
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface SunbedPricingData {
  id: number;
  label: string;
  type: string;
  price: string;
  duration: string | null;
  company_id: number;
}
export interface Room {
  label: string;
  status?: string;
}
export interface SunbedItem {
  id: number;
  company_id: number;
  label: string;
  status: string;
  availability: string;
  created_at: string;
  updated_at: string;
  total_hours: number;
  total_revenue: number;
  pricing_data: SunbedPricingData[];
  pricing: any[];
  rooms?: Room[];
}

export interface GetSunbedsResponse {
  status: string;
  data: SunbedItem[];
}

export interface SingleSunbedItem {
  id: number;
  company_id: number;
  label: string;
  status: string;
  availability: string;
  created_at: string;
  updated_at: string;
  total_hours: number;
  total_revenue: number;
  pricing: SunbedPricingData[];
  booking_items: any[];
}


export interface GetSingleSunbedResponse {
  status: string;
  data: SingleSunbedItem;
}

export const getSingleSunbed = async (
  id: number
): Promise<GetSingleSunbedResponse> => {
  try {
    const response = await api.get(`/admin/sunbeds/${id}`, {
      headers: { Accept: "application/json" },
    });

    console.log("getSingleSunbed response:", response.data);


    return response.data as GetSingleSunbedResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface SunbedPricingData {
  id: number;
  label: string;
  type: string;
  price: string;
  duration: string | null;
  company_id: number;
}

export interface SunbedItem {
  id: number;
  company_id: number;
  label: string;
  status: string;
  availability: string;
  created_at: string;
  updated_at: string;
  total_hours: number;
  total_revenue: number;
  pricing_data: SunbedPricingData[];
  pricing: any[];
  booking_items: any[];
}

export interface GetSunbedsResponse {
  status: string;
  data: SunbedItem[];
}

export const getSunbeds = async (): Promise<GetSunbedsResponse> => {
  try {
    const response = await api.get("/admin/sunbeds", {
      headers: {
        Accept: "application/json",
      },
    });
    console.log("getSunbeds response:", response.data);

    return response.data as GetSunbedsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};



export interface DeleteSunbedResponse {
  status: string;
  message: string;
}

export const deleteSunbed = async (id: number): Promise<DeleteSunbedResponse> => {
  try {
    const response = await api.delete(`/admin/sunbeds/${id}`, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("deleteSunbed response:", response.data);


    return response.data as DeleteSunbedResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};




export interface SunbedBookingItem {
  id: number;
  sunbed_id: number;
  sunbed_mins: number;
  quantity: number;
  unit_price: number;
  amount: number;
  sunbed: {
    id: number;
    label: string;
    status: string;
  };
  pricing: {
    id: number;
    label: string;
    type: string;
    price: string;
    duration: string | null;
  };
}

export interface SunbedBooking {
  id: number;
  date_time: string;
  status: string;
  payment_status: string;
  user: {
    id: number;
    name: string;
    email: string;
    mobile: string;
    user_image_url: string;
  };
  items: SunbedBookingItem[];
}

export interface SunbedBookingsResponse {
  status: string;
  data: {
    bookings: SunbedBooking[];
  };
}

export const getAllSunbedBookings = async (): Promise<SunbedBookingsResponse> => {
  try {
    const response = await api.get("/admin/sunbed-bookings", {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("getAllSunbedBookings response:", response.data);


    return response.data as SunbedBookingsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

// Legacy-compatible helper used by SunbedSessionLogs.
// Currently just proxies to getAllSunbedBookings; the searchParam
// can be wired to backend filtering later if needed.
export const getSunbedBookings = async (searchParam?: string | number) => {
  try {
    const response = await getAllSunbedBookings();
    console.log("getSunbedBookings response (via getAllSunbedBookings):", response);
    return response.data; // { bookings: SunbedBooking[] }
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};

// --- Customer Packages (flattened sunbed packages) ---

// export interface SunbedCustomerPackageApi {
//   id: number;
//   customer: string | null;
//   package: string | null;
//   used_minutes: number;
//   remaining_minutes: number;
//   sunbed_id: number;
//   customer_id: number;
// }

// {
//     "id": 484,
//     "user_id": 17455,
//     "sunbed_id": 50,
//     "company_id": 1,
//     "date": "2025-12-16",
//     "used_minutes": null,
//     "remaining_balance": 11,
//     ...
//     "user": { ... },
//     "sunbed": {
//         ...
//         "rooms": [ ... ]
//     }
// }

export interface SunbedRoomApi {
  id: number;
  label: string;
  availability: string;
  tmax_id?: number; // Added for TMAX integration
  // pivot?: { sunbed_package_id: number; sunbed_room_id: number };
}

export interface SunbedApi {
  id: number;
  label: string;
  rooms: SunbedRoomApi[];
}

export interface SunbedUserApi {
  id: number;
  name: string;
  mobile: string;
  formatted_mobile?: string;
}

export interface SunbedCustomerPackageApi {
  id: number;
  user_id: number;
  sunbed_id: number;
  remaining_balance: number; // Changed from remaining_minutes
  used_minutes: number; // Changed from remaining_minutes
  package_name?: string; // Not in JSON, will use sunbed.label as fallback
  user: SunbedUserApi;
  sunbed: SunbedApi;
}

export interface SunbedCustomerPackagesResponse {
  status: string; // Changed from success: boolean to match typical Laravel API response if consistent
  // or
  success?: boolean;
  data: SunbedCustomerPackageApi[];
}

export const getSunbedCustomerPackages = async (
  sunbedId?: number | string
): Promise<SunbedCustomerPackagesResponse> => {
  try {
    const response = await api.get("/admin/sunbed-sessions/create", {
      headers: { Accept: "application/json" },
      params: sunbedId !== undefined ? { s: sunbedId } : {},
    });
    console.log("getSunbedCustomerPackages response:", response.data);
    return response.data as SunbedCustomerPackagesResponse;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};

export interface UpdateSunbedCustomerPackageResponse {
  status: string;
  message: string;
  data: SunbedCustomerPackageApi;
}

export const updateSunbedCustomerPackage = async (
  id: number | string,
  remainingMins: number
): Promise<UpdateSunbedCustomerPackageResponse> => {
  try {
    const response = await api.put(
      `/admin/sunbed-bookings/${id}`,
      { package_remaining_mins: remainingMins },
      {
        headers: { Accept: "application/json" },
      }
    );
    console.log("updateSunbedCustomerPackage response:", response.data);
    return response.data as UpdateSunbedCustomerPackageResponse;
  } catch (err: any) {
    if (err?.response?.status === 422) {
      // Backend returns a simple message for validation error
      throw {
        type: "server",
        message:
          err.response?.data?.message ||
          "New remaining minutes cannot be greater than the current balance.",
      } as ApiError;
    }
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface CreateSunbedRoomPayload {
  label: string;
}

export interface SunbedRoomResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const createSunbedRoom = async (
  payload: CreateSunbedRoomPayload
): Promise<SunbedRoomResponse> => {
  try {
    const response = await api.post("/admin/sunbed-rooms", payload, {
      headers: { Accept: "application/json" },
    });
    console.log("createSunbedRoom response:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });
      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const getSunbedRooms = async (): Promise<any> => {
  try {
    const response = await api.get("/admin/sunbed-rooms", {
      headers: { Accept: "application/json" },
    });
    console.log("getSunbedRooms response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const updateSunbedRoom = async (id: number | string, payload: CreateSunbedRoomPayload): Promise<SunbedRoomResponse> => {
  try {
    const response = await api.put(`/admin/sunbed-rooms/${id}`, payload, {
      headers: { Accept: "application/json" },
    });
    console.log("updateSunbedRoom response:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });
      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const deleteSunbedRoom = async (id: number | string): Promise<SunbedRoomResponse> => {
  try {
    const response = await api.delete(`/admin/sunbed-rooms/${id}`, {
      headers: { Accept: "application/json" },
    });
    console.log("deleteSunbedRoom response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export interface CreateSunbedSessionPayload {
  user_id: number;
  room_id: number;
  interval: number;
  delay: number;
  package_id: number;
  remaining_balance: number;
}


export interface SunbedSessionResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const createSunbedSession = async (
  payload: CreateSunbedSessionPayload
): Promise<SunbedSessionResponse> => {
  try {
    const response = await api.post("/admin/sunbed-sessions", payload, {
      headers: { Accept: "application/json" },
    });
    console.log("createSunbedSession response:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });
      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export interface SunbedSessionApiItem {
  id: number;
  sunbed_room: string;
  tmax_id?: number | string; // Added for TMAX integration
  customer: string;
  mins_left: {
    time: string; // e.g. "05:00"
    remaining: number; // seconds
    delay: number; // seconds
  };
  total_duration: string;
  status: {
    label: string;
    badge_class: string;
  };
  action: {
    type: string;
    url: string;
    tooltip: string;
  } | null;
}

export interface GetSunbedSessionsResponse {
  status: string;
  data: SunbedSessionApiItem[];
}

export const getSunbedSessions = async (searchParam?: string): Promise<GetSunbedSessionsResponse> => {
  try {
    const response = await api.get("/admin/sunbed-sessions", {
      headers: { Accept: "application/json" },
      params: searchParam ? { s: searchParam } : {},
    });
    console.log("getSunbedSessions response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const updateSunbedSession = async (id: number | string, payload: Partial<CreateSunbedSessionPayload>): Promise<SunbedSessionResponse> => {
  try {
    const response = await api.put(`/admin/sunbed-sessions/${id}`, payload, {
      headers: { Accept: "application/json" },
    });
    console.log("updateSunbedSession response:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });
      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const deleteSunbedSession = async (id: number | string): Promise<SunbedSessionResponse> => {
  try {
    const response = await api.delete(`/admin/sunbed-sessions/${id}`, {
      headers: { Accept: "application/json" },
    });
    console.log("deleteSunbedSession response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  };


};

export const finishSunbedSession = async (id: number | string): Promise<SunbedSessionResponse> => {
  try {
    const response = await api.post(`/admin/sunbed-sessions/finished/${id}`, {}, {
      headers: { Accept: "application/json" },
    });
    console.log("finishSunbedSession response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const cancelSunbedSession = async (id: number | string): Promise<SunbedSessionResponse> => {
  try {
    const response = await api.post(`/admin/sunbed-sessions/cancel/${id}`, {}, {
      headers: { Accept: "application/json" },
    });
    console.log("cancelSunbedSession response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export interface SunbedSessionLogApiItem {
  customer: string;
  sunbed_details: string;
  actual_package_mins: string;
  session_duration: string; // HTML string
  created_by: string;
  status: {
    label: string;
    badge_class: string;
  };
}

export interface GetSunbedSessionLogsResponse {
  status: string;
  data: SunbedSessionLogApiItem[];
}

export const getSunbedSessionLogs = async (searchParam?: string): Promise<GetSunbedSessionLogsResponse> => {
  try {
    const response = await api.get("/admin/sunbed-session-logs", {
      headers: { Accept: "application/json" },
      params: searchParam ? { s: searchParam } : {},
    });
    console.log("getSunbedSessionLogs response:", response.data);
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw { type: "network", message: "Network error" };
    }
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};